export type LatexTextSegment = {
  type: 'text';
  content: string;
};

export type LatexMathSegment = {
  type: 'math';
  /** LaTeX source without the surrounding delimiters. */
  content: string;
  /** Original substring including delimiters, used as a plain-text fallback. */
  raw: string;
  displayMode: boolean;
  /** True when the closing delimiter is missing (e.g. mid typewriter animation). */
  incomplete: boolean;
};

export type LatexSegment = LatexTextSegment | LatexMathSegment;

type Delimiter = {
  open: string;
  close: string;
  displayMode: boolean;
};

// Order matters: `$$` must be matched before `$`.
const DELIMITERS: Delimiter[] = [
  { open: '$$', close: '$$', displayMode: true },
  { open: '\\[', close: '\\]', displayMode: true },
  { open: '\\(', close: '\\)', displayMode: false },
  { open: '$', close: '$', displayMode: false },
];

const isEscapedDollar = (text: string, index: number) => text[index] === '$' && index > 0 && text[index - 1] === '\\';

const findClosingIndex = (text: string, fromIndex: number, close: string) => {
  let searchIndex = fromIndex;
  while (searchIndex < text.length) {
    const candidate = text.indexOf(close, searchIndex);
    if (candidate === -1) {
      return -1;
    }
    if (close.startsWith('$') && isEscapedDollar(text, candidate)) {
      searchIndex = candidate + 1;
      continue;
    }
    return candidate;
  }
  return -1;
};

const matchDelimiterAt = (text: string, index: number): Delimiter | null => {
  for (const delimiter of DELIMITERS) {
    if (!text.startsWith(delimiter.open, index)) {
      continue;
    }
    if (delimiter.open === '$' && isEscapedDollar(text, index)) {
      return null;
    }
    return delimiter;
  }
  return null;
};

/**
 * Splits text into plain-text and LaTeX math segments. Supported delimiters:
 * `$$…$$`, `\[…\]` (display mode), `\(…\)` and `$…$` (inline). A `$…$` pair is
 * only treated as math when the inner content is non-empty, single-line, and not
 * padded with whitespace — this avoids false positives on ordinary dollar signs.
 */
export const parseLatexSegments = (text: string): LatexSegment[] => {
  const segments: LatexSegment[] = [];
  let textStart = 0;
  let index = 0;

  const flushText = (end: number) => {
    if (end > textStart) {
      segments.push({ type: 'text', content: text.slice(textStart, end) });
    }
  };

  while (index < text.length) {
    const delimiter = matchDelimiterAt(text, index);
    if (!delimiter) {
      index += 1;
      continue;
    }

    const contentStart = index + delimiter.open.length;
    const closingIndex = findClosingIndex(text, contentStart, delimiter.close);

    // `$…$` is ambiguous (currency, prices); require math-looking content:
    // non-empty, not whitespace-padded, single line.
    const isPlausibleSingleDollarMath = (content: string) =>
      content.trim().length > 0 && !/^\s/.test(content) && !content.includes('\n');

    if (closingIndex === -1) {
      const remainder = text.slice(contentStart);
      if (delimiter.open === '$' && !isPlausibleSingleDollarMath(remainder) && remainder.length > 0) {
        index += 1;
        continue;
      }
      flushText(index);
      segments.push({
        type: 'math',
        content: remainder,
        raw: text.slice(index),
        displayMode: delimiter.displayMode,
        incomplete: true,
      });
      return segments;
    }

    const content = text.slice(contentStart, closingIndex);

    if (delimiter.open === '$' && (!isPlausibleSingleDollarMath(content) || content !== content.trim())) {
      index += 1;
      continue;
    }

    flushText(index);
    segments.push({
      type: 'math',
      content,
      raw: text.slice(index, closingIndex + delimiter.close.length),
      displayMode: delimiter.displayMode,
      incomplete: false,
    });
    index = closingIndex + delimiter.close.length;
    textStart = index;
  }

  flushText(text.length);
  return segments;
};

/** True when the text contains at least one complete LaTeX math fragment. */
export const containsLatex = (text: string) =>
  parseLatexSegments(text).some(segment => segment.type === 'math' && !segment.incomplete);
